import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage({ onLogin }) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const handlePinChange = (digit) => {
    if (pin.length < 6) {
      setPin(pin + digit);
    }
  };

  const handleBackspace = () => {
    setPin(pin.slice(0, -1));
  };

  const handleLogin = async () => {
    if (pin.length < 4) {
      toast.error("PIN must be at least 4 digits");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { pin });
      localStorage.setItem("tinkah_token", res.data.token);
      localStorage.setItem("tinkah_user", JSON.stringify(res.data.user));
      onLogin(res.data.user);
      toast.success(`Welcome, ${res.data.user.name}`);
    } catch (err) {
      toast.error("Invalid PIN");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const digits = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"];

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex flex-col items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-12">
          <img src="/tinkah-logo.png" alt="TINKAH" className="h-16 mx-auto mb-4" />
          <p className="font-['Space_Mono'] text-xs tracking-[0.3em] uppercase text-[#8B7536]">
            Bespoke Embroidery
          </p>
        </div>

        <div className="bg-white border border-[#D6D6CF] p-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Lock className="w-5 h-5 text-[#9F7D23]" />
            <h2 className="font-['Space_Mono'] text-sm tracking-widest uppercase text-[#5C4D24]">
              Enter PIN
            </h2>
          </div>

          {/* PIN Display */}
          <div className="flex justify-center gap-3 mb-8">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className={`w-10 h-12 border-2 flex items-center justify-center font-['Space_Mono'] text-xl transition-all ${
                  i < pin.length 
                    ? "border-[#9F7D23] bg-[#9F7D23]/10" 
                    : "border-[#D6D6CF]"
                }`}
              >
                {i < pin.length ? "•" : ""}
              </div>
            ))}
          </div>

          {/* PIN Pad */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {digits.map((digit, i) => (
              <button
                key={i}
                onClick={() => {
                  if (digit === "⌫") handleBackspace();
                  else if (digit) handlePinChange(digit);
                }}
                disabled={!digit || loading}
                className={`h-14 font-['Space_Mono'] text-xl transition-all ${
                  digit
                    ? "bg-[#F5F5F0] border border-[#D6D6CF] text-[#5C4D24] hover:bg-[#9F7D23]/10 hover:border-[#9F7D23] active:scale-95"
                    : "invisible"
                }`}
                data-testid={digit ? `pin-${digit === "⌫" ? "backspace" : digit}` : undefined}
              >
                {digit}
              </button>
            ))}
          </div>

          {/* Login Button */}
          <button
            onClick={handleLogin}
            disabled={loading || pin.length < 4}
            data-testid="login-btn"
            className="w-full py-4 bg-[#9F7D23] text-white font-['Space_Mono'] text-sm tracking-widest uppercase hover:bg-[#8A6D1F] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
              </>
            ) : (
              "Login"
            )}
          </button>
        </div>

        <p className="text-center mt-8 font-['Space_Mono'] text-[10px] tracking-[0.2em] uppercase text-[#8B7536]/60">
          Crafted with Intention
        </p>
      </div>
    </div>
  );
}
